import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { CreditCard, Truck, Package, CheckCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { trackEvent } from '../seo/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, getCartTotal, clearCart } = useCart();
  const [step, setStep] = useState(1); // 1: Shipping, 2: Payment, 3: Complete
  const [isProcessing, setIsProcessing] = useState(false);
  const [shippingQuote, setShippingQuote] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');

  const [shippingAddress, setShippingAddress] = useState({
    name: '',
    email: '',
    phone: '',
    address_line_1: '',
    city: '',
    state: '',
    postal_code: ''
  });

  const [customerInfo, setCustomerInfo] = useState({
    email: location.state?.memberEmail || '',
    name: '',
    phone: ''
  });

  useEffect(() => {
    if (cart.length === 0 && step < 3) {
      navigate('/cart');
    }
  }, [cart, navigate, step]);

  const handleAddressChange = (e) => {
    setShippingAddress({
      ...shippingAddress,
      [e.target.name]: e.target.value
    });
  };

  const calculateShipping = async () => {
    try {
      const totalWeight = cart.reduce((weight, item) => weight + (30 * item.quantity), 0);
      
      const response = await axios.post(`${BACKEND_URL}/api/shipping/quote`, {
        destination: {
          name: shippingAddress.name,
          address_line_1: shippingAddress.address_line_1,
          city: shippingAddress.city,
          state: shippingAddress.state,
          postal_code: shippingAddress.postal_code
        },
        package_weight: totalWeight,
        tire_quantity: cart.reduce((sum, item) => sum + item.quantity, 0)
      });

      if (response.data.quotes && response.data.quotes.length > 0) {
        setShippingQuote(response.data.quotes[0]);
        return response.data.quotes[0].price;
      }
      return 0;
    } catch (error) {
      toast.error('Failed to calculate shipping');
      return 0;
    }
  };

  const handleShippingSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    
    try {
      await calculateShipping();
      setStep(2);
    } catch (error) {
      toast.error('Please check your shipping address');
    } finally {
      setIsProcessing(false);
    }
  };

  const createOrder = async () => {
    try {
      const orderData = {
        customer_email: shippingAddress.email,
        customer_name: shippingAddress.name,
        customer_phone: shippingAddress.phone,
        items: cart.map(item => ({
          tire_size: item.size,
          quantity: item.quantity,
          price: item.final_price || item.price
        })),
        subtotal: getCartTotal(),
        shipping_address: shippingAddress,
        shipping_cost: shippingQuote?.price || 0,
        state_tax: stateTax,
        county_tax: countyTax,
        total_tax: totalTax,
        total: getCartTotal() + (shippingQuote?.price || 0) + totalTax
      };

      const response = await axios.post(`${BACKEND_URL}/api/orders`, orderData);
      return response.data.order_number;
    } catch (error) {
      throw new Error('Failed to create order');
    }
  };

  const handlePayPalPayment = async () => {
    setIsProcessing(true);
    
    try {
      // Create order first
      const orderNum = await createOrder();
      setOrderNumber(orderNum);
      
      const totalAmount = getCartTotal() + (shippingQuote?.price || 0);
      
      // Create PayPal payment
      const paymentResponse = await axios.post(`${BACKEND_URL}/api/payments/create`, {
        order_id: orderNum,
        amount: totalAmount,
        currency: 'USD',
        return_url: `${window.location.origin}/checkout/success`,
        cancel_url: `${window.location.origin}/checkout`
      });

      if (paymentResponse.data.success && paymentResponse.data.approval_url) {
        // Redirect to PayPal
        window.location.href = paymentResponse.data.approval_url;
      } else {
        throw new Error('Payment creation failed');
      }
    } catch (error) {
      toast.error('Payment failed', {
        description: error.message
      });
      setIsProcessing(false);
    }
  };

  const handleDirectPayment = async () => {
    setIsProcessing(true);
    
    try {
      const orderNum = await createOrder();
      setOrderNumber(orderNum);
      
      // Mark as pending payment
      await axios.patch(`${BACKEND_URL}/api/orders/${orderNum}`, {
        status: 'Pending Payment',
        payment_method: 'cash_on_delivery'
      });

      // GA4 conversion: purchase
      const subtotal = getCartTotal();
      const shippingCost = shippingQuote?.price || 0;
      const stateTax = subtotal * 0.06;
      const countyTax = subtotal * 0.015;
      const totalValue = subtotal + shippingCost + stateTax + countyTax;
      trackEvent('purchase', {
        transaction_id: orderNum,
        value: Number(totalValue.toFixed(2)),
        currency: 'USD',
        tax: Number((stateTax + countyTax).toFixed(2)),
        shipping: Number(shippingCost.toFixed(2)),
        items: cart.map((item) => ({
          item_id: item.size,
          item_name: `Tire ${item.size}`,
          price: Number((item.final_price || item.price).toFixed(2)),
          quantity: item.quantity,
        })),
      });

      clearCart();
      setStep(3);
      toast.success('Order placed successfully!');
    } catch (error) {
      toast.error('Order failed', {
        description: error.message
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Calculate taxes (Florida only)
  const stateTax = shippingAddress.state === 'FL' ? getCartTotal() * 0.06 : 0;
  const countyTax = shippingAddress.state === 'FL' ? getCartTotal() * 0.015 : 0;
  const totalTax = stateTax + countyTax;
  
  const totalWithShipping = getCartTotal() + (shippingQuote?.price || 0) + totalTax;

  if (step === 3) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Order Placed!</h2>
            <p className="text-gray-600 mb-6">
              Your order <strong>#{orderNumber}</strong> has been received.
            </p>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-bold text-gray-900 mb-2">What's Next?</h3>
              <ul className="text-sm text-gray-700 space-y-1">
                <li>✓ Order confirmation sent to {shippingAddress.email}</li>
                <li>✓ We'll prepare your tires for shipment</li>
                <li>✓ Estimated delivery: {shippingQuote?.estimated_delivery_days || 3-5} business days</li>
              </ul>
            </div>

            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/')} className="bg-red-600 hover:bg-red-700">
                Back to Home
              </Button>
              <Button onClick={() => navigate('/contact')} variant="outline">
                Contact Us
              </Button>
            </div>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center">
            <div className={`flex items-center ${step >= 1 ? 'text-red-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step >= 1 ? 'bg-red-600 text-white' : 'bg-gray-300'
              }`}>
                1
              </div>
              <span className="ml-2 font-medium">Shipping</span>
            </div>
            
            <div className={`w-16 h-1 mx-4 ${step >= 2 ? 'bg-red-600' : 'bg-gray-300'}`} />
            
            <div className={`flex items-center ${step >= 2 ? 'text-red-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                step >= 2 ? 'bg-red-600 text-white' : 'bg-gray-300'
              }`}>
                2
              </div>
              <span className="ml-2 font-medium">Payment</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {step === 1 && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <Truck className="w-6 h-6 text-red-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Shipping Address</h2>
                </div>

                <form onSubmit={handleShippingSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        name="name"
                        value={shippingAddress.name}
                        onChange={handleAddressChange}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input
                        type="email"
                        name="email"
                        value={shippingAddress.email}
                        onChange={handleAddressChange}
                        required
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Phone Number *</Label>
                    <Input
                      type="tel"
                      name="phone"
                      value={shippingAddress.phone}
                      onChange={handleAddressChange}
                      required
                      placeholder="(305) 555-0123"
                    />
                  </div>

                  <div>
                    <Label>Street Address *</Label>
                    <Input
                      name="address_line_1"
                      value={shippingAddress.address_line_1}
                      onChange={handleAddressChange}
                      required
                      placeholder="123 Main St"
                    />
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>City *</Label>
                      <Input
                        name="city"
                        value={shippingAddress.city}
                        onChange={handleAddressChange}
                        required
                        placeholder="Miami"
                      />
                    </div>
                    <div>
                      <Label>State *</Label>
                      <select
                        name="state"
                        value={shippingAddress.state}
                        onChange={handleAddressChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="">Select State</option>
                        <option value="FL">Florida</option>
                        <option value="GA">Georgia</option>
                        <option value="AL">Alabama</option>
                        <option value="SC">South Carolina</option>
                        <option value="NC">North Carolina</option>
                        <option value="TN">Tennessee</option>
                        {/* Add more states */}
                      </select>
                    </div>
                    <div>
                      <Label>ZIP Code *</Label>
                      <Input
                        name="postal_code"
                        value={shippingAddress.postal_code}
                        onChange={handleAddressChange}
                        required
                        placeholder="33101"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    disabled={isProcessing}
                    className="w-full bg-red-600 hover:bg-red-700"
                  >
                    {isProcessing ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Calculating Shipping...
                      </>
                    ) : (
                      'Continue to Payment'
                    )}
                  </Button>
                </form>
              </Card>
            )}

            {step === 2 && (
              <Card className="p-6">
                <div className="flex items-center gap-3 mb-6">
                  <CreditCard className="w-6 h-6 text-red-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Payment Method</h2>
                </div>

                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <p className="text-sm text-blue-800">
                      <strong>Shipping to:</strong><br />
                      {shippingAddress.name}<br />
                      {shippingAddress.address_line_1}<br />
                      {shippingAddress.city}, {shippingAddress.state} {shippingAddress.postal_code}
                    </p>
                  </div>

                  <Button
                    onClick={handlePayPalPayment}
                    disabled={isProcessing}
                    className="w-full bg-blue-600 hover:bg-blue-700 h-12"
                  >
                    {isProcessing ? (
                      <>
                        <Loader className="w-5 h-5 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.03a.641.641 0 0 1 .633-.54h4.606a.641.641 0 0 1 .633.74L7.71 20.797a.641.641 0 0 1-.633.54z"/>
                          <path d="M15.31 21.337h-4.606a.641.641 0 0 1-.633-.74L13.177 3.03a.641.641 0 0 1 .633-.54h4.606a.641.641 0 0 1 .633.74l-3.105 17.567a.641.641 0 0 1-.633.54z"/>
                        </svg>
                        Pay with PayPal
                      </>
                    )}
                  </Button>

                  <div className="relative my-6">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-2 bg-white text-gray-500">OR</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleDirectPayment}
                    disabled={isProcessing}
                    variant="outline"
                    className="w-full h-12"
                  >
                    Pay on Delivery (Cash/Card)
                  </Button>

                  <Button
                    onClick={() => setStep(1)}
                    variant="ghost"
                    className="w-full"
                  >
                    ← Back to Shipping
                  </Button>
                </div>
              </Card>
            )}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="p-6 sticky top-4">
              <div className="flex items-center gap-2 mb-4">
                <Package className="w-5 h-5 text-red-600" />
                <h3 className="font-bold text-gray-900">Order Summary</h3>
              </div>

              <div className="space-y-3 mb-4">
                {cart.map((item) => (
                  <div key={item.size} className="flex justify-between text-sm">
                    <span className="text-gray-600">
                      {item.size} x {item.quantity}
                    </span>
                    <span className="font-semibold">
                      ${((item.final_price || item.price) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">${getCartTotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span className="font-semibold">
                    {shippingQuote ? `$${shippingQuote.price.toFixed(2)}` : 'TBD'}
                  </span>
                </div>
                
                {shippingAddress.state === 'FL' && (
                  <>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Florida State Tax (6%)</span>
                      <span className="font-semibold">${stateTax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">County Surcharge (1.5%)</span>
                      <span className="font-semibold">${countyTax.toFixed(2)}</span>
                    </div>
                  </>
                )}
                
                <div className="border-t pt-2 flex justify-between">
                  <span className="font-bold text-gray-900">Total</span>
                  <span className="text-xl font-bold text-green-600">
                    ${totalWithShipping.toFixed(2)}
                  </span>
                </div>
              </div>

              {shippingQuote && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-xs text-green-800">
                    <strong>Estimated Delivery:</strong><br />
                    {shippingQuote.estimated_delivery_days} business days
                  </p>
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};
